import { isMatchUpcoming } from '@/lib/matchTime';
import { appUrl } from '@/lib/appUrl';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { leaguesAPI, matchesAPI, competitionsAPI, traitsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Trophy, Users, MapPin, Calendar, Settings, Share2, Lock, Globe, Plus, ChevronRight, Copy, Check, LogOut, UserPlus, BarChart3, ShieldCheck, ShieldOff, UserX, Crown, Tag, IdCard,
} from 'lucide-react';
import { 
  getInitials, formatDate, getStatusColorClass, getStatusLabel, getRoleLabel, resolveMediaUrl 
} from '@/lib/utils';
import { LeagueLeaderboard } from '@/components/LeagueLeaderboard';
import { LeagueMembersManager } from '@/components/LeagueMembersManager';
import { LeagueImportPanel } from '@/components/LeagueImportPanel';
import { ScoringSettings, PointAdjustments, MemberStatsEditor, SeasonReset } from '@/components/LeagueScoringManager';
import { PlayerProfileDialog } from '@/components/PlayerProfileDialog';
import { MemberProfileDialog } from '@/components/MemberProfileDialog';
import { PlayerTraitsRow } from '@/components/PlayerTraits';
import { TopScorersTab } from '@/components/TopScorersTab';
import { LeagueSeriesManager } from '@/components/LeagueSeriesManager';
import { FifaCard, CardLegend } from '@/components/FifaCard';

export const LeagueDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState(null);
  const [sport, setSport] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [userMembership, setUserMembership] = useState(null);
  const [matches, setMatches] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [joining, setJoining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteRequired, setInviteRequired] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [editProfileMember, setEditProfileMember] = useState(null);
  const [traitsCatalog, setTraitsCatalog] = useState([]);
  const [membersView, setMembersView] = useState('cards');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leagueRes, matchesRes, competitionsRes] = await Promise.all([
          leaguesAPI.getById(id, inviteCode ? { invite_code: inviteCode } : undefined),
          matchesAPI.getAll({ league_id: id }),
          competitionsAPI.getAll({ league_id: id })
        ]);
        
        setLeague(leagueRes.data.league);
        setSport(leagueRes.data.sport);
        setMembers(leagueRes.data.members);
        setPendingMembers(leagueRes.data.pending_members || []);
        setUserMembership(leagueRes.data.user_membership);
        setMatches(matchesRes.data);
        setCompetitions(competitionsRes.data || []);
        setInviteRequired(false);
      } catch (error) {
        if (error.response?.status === 403) {
          setInviteRequired(true);
        } else {
          console.error('Error fetching league:', error);
          toast.error('Errore nel caricamento della lega');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleInviteAccess = async () => {
    if (!inviteCode) {
      toast.error('Inserisci un codice invito');
      return;
    }
    setLoading(true);
    try {
      const [leagueRes, matchesRes, competitionsRes] = await Promise.all([
        leaguesAPI.getById(id, { invite_code: inviteCode }),
        matchesAPI.getAll({ league_id: id }),
        competitionsAPI.getAll({ league_id: id })
      ]);
      setLeague(leagueRes.data.league);
      setSport(leagueRes.data.sport);
      setMembers(leagueRes.data.members);
      setPendingMembers(leagueRes.data.pending_members || []);
      setUserMembership(leagueRes.data.user_membership);
      setMatches(matchesRes.data);
      setCompetitions(competitionsRes.data || []);
      setInviteRequired(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice invito non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      await leaguesAPI.join(id, { invite_code: inviteCode || undefined });
      toast.success(league.is_public ? 'Sei entrato nella lega!' : 'Richiesta inviata!');
      setShowJoinDialog(false);
      // Refresh data
      const leagueRes = await leaguesAPI.getById(id);
      setUserMembership(leagueRes.data.user_membership);
      setMembers(leagueRes.data.members);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'iscrizione');
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Sei sicuro di voler abbandonare questa lega?')) return;
    
    try {
      await leaguesAPI.leave(id);
      toast.success('Hai abbandonato la lega');
      setUserMembership(null);
      // Refresh members
      const leagueRes = await leaguesAPI.getById(id);
      setMembers(leagueRes.data.members);
    } catch (error) {
      toast.error('Errore nell\'uscita dalla lega');
    }
  };

  const copyInviteLink = () => {
    const link = appUrl(`/leagues/${id}?code=${encodeURIComponent(league.invite_code)}`);
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copiato!');
  };

  const isAdmin = userMembership?.role === 'admin' || userMembership?.role === 'manager' || user?.role === 'admin';

  // Every section one tap away; the tab stays in the URL, so coming back from a match reopens it.
  const leagueTabs = [
    ['overview', 'In breve'], ['matches', 'Partite'], ['leaderboard', 'Classifica'],
    ['members', `Membri (${members.length})`], ['scorers', 'Cannonieri'], ['mvp', 'MVP'],
    ['competitions', `Tornei (${competitions.length})`],
    ...(isAdmin ? [['series', 'Ripetizioni'], ['admin', 'Gestione']] : []),
  ];
  const selectTab = (tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const isOwner = userMembership?.is_owner === true;

  useEffect(() => {
    traitsAPI.getAll(league?.sport_id)
      .then((res) => setTraitsCatalog(res.data.traits || []))
      .catch(() => {/* catalog non critico */});
  }, [league?.sport_id]);

  // A section further along the tab bar scrolls into sight.
  useEffect(() => {
    document.querySelector('[role="tab"][data-state="active"]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [activeTab, loading]);

  const renderMemberMeta = (member) => (
    <PlayerTraitsRow
      className="mt-1.5"
      size="md"
      traits={member.traits || []}
      skillLevel={member.skill_level}
      form={member.form}
      formBreakdown={member.attributes?.form_breakdown}
      preferredFoot={member.attributes?.preferred_foot}
      catalog={traitsCatalog}
    />
  );

  const handleProfileSaved = (updated) => {
    setMembers((prev) => prev.map((m) =>
      m.user_id === updated.user_id
        ? { ...m, traits: updated.traits, skill_level: updated.skill_level, form: updated.form, attributes: updated.attributes || m.attributes }
        : m
    ));
  };

  const handleChangeRole = async (targetUserId, newRole) => {
    try {
      await leaguesAPI.changeMemberRole(id, targetUserId, { role: newRole });
      toast.success(`Ruolo aggiornato a ${newRole === 'admin' ? 'Admin' : 'Giocatore'}`);
      const leagueRes = await leaguesAPI.getById(id);
      setMembers(leagueRes.data.members);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel cambio ruolo');
    }
  };

  const handleJoinRequest = async (targetUserId, approve) => {
    try {
      await leaguesAPI.updateMember(id, targetUserId, { status: approve ? 'approved' : 'rejected' });
      toast.success(approve ? 'Richiesta approvata' : 'Richiesta rifiutata');
      const leagueRes = await leaguesAPI.getById(id);
      setMembers(leagueRes.data.members);
      setPendingMembers(leagueRes.data.pending_members || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella gestione della richiesta');
    }
  };

  const handleKickMember = async (targetUserId, memberName) => {
    if (!window.confirm(`Sei sicuro di voler espellere ${memberName} dalla lega?`)) return;
    try {
      await leaguesAPI.kickMember(id, targetUserId);
      toast.success(`${memberName} è stato espulso dalla lega`);
      setMembers(prev => prev.filter(m => m.user_id !== targetUserId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'espulsione');
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (inviteRequired) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-xl mx-auto space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Accesso solo su invito</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Questa lega è privata. Inserisci il codice invito per continuare.
            </p>
            <div className="space-y-2">
              <Label htmlFor="invite-code-access">Codice Invito</Label>
              <Input
                id="invite-code-access"
                placeholder="Es: ABC123XY"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/leagues')}>
                Torna alle leghe
              </Button>
              <Button onClick={handleInviteAccess}>
                Accedi
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="p-4 md:p-6 lg:p-8 text-center">
        <Trophy className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Lega non trovata</h2>
        <Link to="/leagues">
          <Button>Torna alle leghe</Button>
        </Link>
      </div>
    );
  }

  // ─── derived metrics for the new overview ───────────────────────────────────
  const upcomingMatch = matches
    .filter((m) => isMatchUpcoming(m))
    .sort((a, b) => new Date(a.date_time) - new Date(b.date_time))[0];

  const completedCount = matches.filter((m) => m.status === 'completed').length;

  const formatRelativeShort = (iso) => {
    if (!iso) return '';
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return 'ora';
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days >= 1) return `Tra ${days}g ${hours % 24}h`;
    if (hours >= 1) return `Tra ${hours}h ${minutes % 60}m`;
    return `Tra ${minutes}m`;
  };

  return (
    <div data-testid="league-detail">
      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <div className="relative h-44 md:h-48 bg-gradient-to-br from-blue-500 to-indigo-700 overflow-hidden">
        {league.cover_image_url && (
          <img
            src={resolveMediaUrl(league.cover_image_url)}
            alt={league.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Strong gradient overlay so titles/buttons stay legible over any cover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/20" />

        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-8 py-4 md:py-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 max-w-7xl mx-auto">
              <div className="text-white min-w-0">
                <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest font-semibold">
                    {league.is_public ? <><Globe className="h-2.5 w-2.5" /> Pubblica</> : <><Lock className="h-2.5 w-2.5" /> Privata</>}
                  </span>
                  {sport?.name && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest font-semibold">
                      {sport.name}
                    </span>
                  )}
                </div>
                <h1 className="text-lg sm:text-2xl md:text-4xl font-bold leading-tight drop-shadow-lg line-clamp-2">
                  {league.name}
                </h1>
                {league.city && (
                  <p className="text-white/85 flex items-center gap-1 mt-1 text-xs md:text-base">
                    <MapPin className="h-3.5 w-3.5" /> {league.city}
                  </p>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                {userMembership?.status === 'approved' ? (
                  <>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/leagues/${id}/edit`)}
                        className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white backdrop-blur-sm"
                      >
                        <Settings className="h-4 w-4 sm:mr-2" />
                        <span>Gestisci</span>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLeave}
                      className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white backdrop-blur-sm"
                    >
                      <LogOut className="h-4 w-4 sm:mr-2" />
                      <span>Esci</span>
                    </Button>
                  </>
                ) : userMembership?.status === 'pending' ? (
                  <Button variant="outline" size="sm" disabled className="bg-white/15 border-white/30 text-white">
                    In attesa
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setShowJoinDialog(true)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg"
                    data-testid="join-league-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Unisciti
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ────────────────────────────────────────────────────── */}
      <div className="px-4 md:px-8 pt-6 pb-10">
        <div className="max-w-7xl mx-auto">
          {pendingMembers.length > 0 && (
            <Card className="mb-6 border-primary/40" data-testid="pending-requests">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Richieste in attesa ({pendingMembers.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {pendingMembers.map((member) => (
                  <div key={member.member_id} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar>
                        <AvatarImage src={member.user?.picture} />
                        <AvatarFallback>{getInitials(member.user?.name)}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium truncate">{member.user?.name}</span>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" onClick={() => handleJoinRequest(member.user_id, true)}>Approva</Button>
                      <Button size="sm" variant="outline" onClick={() => handleJoinRequest(member.user_id, false)}>Rifiuta</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <Tabs value={activeTab} onValueChange={selectTab} className="space-y-6">
            {/* Sticky tab bar on mobile so users keep navigation while scrolling */}
            <div className="safe-content-sticky sticky z-20 -mx-4 md:mx-0 px-4 md:px-0 py-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b md:border-b-0 md:bg-transparent md:backdrop-blur-none">
              <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {leagueTabs.map(([value, label]) => (
                  <TabsTrigger key={value} value={value} className="shrink-0">{label}</TabsTrigger>
                ))}
              </TabsList>
            </div>

          <TabsContent value="overview" className="space-y-6">
            {/* Hero "Prossima partita" — visibilissima */}
            {upcomingMatch && (
              <Link to={`/matches/${upcomingMatch.match_id}`} className="block">
                <Card className="overflow-hidden border-2 border-emerald-200 dark:border-emerald-900 hover:shadow-md transition-shadow">
                  <div className="bg-gradient-to-r from-emerald-500 to-blue-500 text-white px-4 py-2 flex items-center justify-between text-sm font-medium">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" />
                      Prossima partita · {formatRelativeShort(upcomingMatch.date_time)}
                    </span>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                      {formatDate(upcomingMatch.date_time, { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg leading-tight">{upcomingMatch.title}</h3>
                        <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{upcomingMatch.location_name}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5" />
                            {upcomingMatch.participants_count || 0}/{upcomingMatch.max_participants || 10}
                          </span>
                          {upcomingMatch.referee && (
                            <span
                              className="flex items-center gap-1.5 hover:text-foreground"
                            >
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={upcomingMatch.referee.picture} />
                                <AvatarFallback className="text-[9px]">
                                  {getInitials(upcomingMatch.referee.name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="truncate max-w-[120px]">
                                Arbitro: {upcomingMatch.referee.name}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Description + Recent Matches */}
              <div className="lg:col-span-2 space-y-6">


                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base">Partite Recenti</CardTitle>
                    {isAdmin && (
                      <Link to={`/matches/new?league=${id}`}>
                        <Button size="sm" className="bg-gradient-to-br from-blue-500 to-indigo-600">
                          <Plus className="h-4 w-4 mr-2" />
                          Nuova
                        </Button>
                      </Link>
                    )}
                  </CardHeader>
                  <CardContent>
                    {matches.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4 text-sm">
                        Nessuna partita programmata
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {matches.slice(0, 5).map((match) => {
                          const hasResult = match.status === 'completed' && match.result;
                          return (
                            <Link
                              key={match.match_id}
                              to={`/matches/${match.match_id}`}
                              className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-muted/60 transition-colors"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{match.title}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(match.date_time, { day: 'numeric', month: 'short' })}
                                  <MapPin className="h-3 w-3 ml-1" />
                                  <span className="truncate">{match.location_name}</span>
                                </div>
                              </div>
                              {hasResult ? (
                                <div className="flex items-center gap-1.5 text-sm font-bold tabular-nums">
                                  <span>{match.result.team_a_score}</span>
                                  <span className="text-muted-foreground">-</span>
                                  <span>{match.result.team_b_score}</span>
                                </div>
                              ) : (
                                <Badge className={getStatusColorClass(match.status)}>
                                  {getStatusLabel(match.status)}
                                </Badge>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar: invite + quick info + actions */}
              <div className="space-y-6">
                {isAdmin && !league.is_public && (
                  <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-900">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Share2 className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-semibold">Invita giocatori</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        Condividi il codice o il link per far entrare nuovi membri.
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-card px-3 py-2 rounded-lg text-sm font-mono font-bold text-center tracking-wider border">
                          {league.invite_code}
                        </code>
                        <Button size="icon" variant="outline" aria-label="Copia link invito" onClick={copyInviteLink}>
                          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {isAdmin && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Azioni rapide</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Link to={`/matches/new?league=${id}`} className="block">
                        <Button variant="outline" className="w-full justify-start">
                          <Plus className="h-4 w-4 mr-2 text-emerald-600" />
                          Nuova partita
                        </Button>
                      </Link>
                      <Link to={`/competitions/new?league=${id}`} className="block">
                        <Button variant="outline" className="w-full justify-start">
                          <Trophy className="h-4 w-4 mr-2 text-amber-500" />
                          Nuova competizione
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Descrizione</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {league.description || 'Nessuna descrizione disponibile.'}
                    </p>
                    {league.rules && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-medium mb-2">Regolamento</h4>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{league.rules}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Info</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Sport</span>
                      <Badge variant="secondary">{sport?.name}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Creata</span>
                      <span className="font-medium">{formatDate(league.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Visibilità</span>
                      <span className="font-medium flex items-center gap-1">
                        {league.is_public ? <><Globe className="h-3.5 w-3.5" /> Pubblica</> : <><Lock className="h-3.5 w-3.5" /> Privata</>}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>


          <TabsContent value="leaderboard">
            <LeagueLeaderboard leagueId={id} />
          </TabsContent>

          <TabsContent aria-label="Cannonieri" value="scorers">
            <TopScorersTab scope="league" scopeId={id} />
          </TabsContent>

          <TabsContent aria-label="MVP" value="mvp">
            <TopScorersTab scope="league" scopeId={id} kind="mvp" />
          </TabsContent>

          <TabsContent aria-label="Tornei" value="competitions" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Competizioni</h2>
              {isAdmin && (
                <Link to={`/competitions/new?league=${id}`}>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuova Competizione
                  </Button>
                </Link>
              )}
            </div>

            {competitions.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nessuna competizione creata</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {competitions.map((comp) => (
                  <Link key={comp.competition_id} to={`/competitions/${comp.competition_id}`}>
                    <Card className="card-interactive">
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{comp.competition_type === 'tournament' ? 'Torneo' : 'Campionato'}</Badge>
                          <Badge variant="outline">{comp.registration_mode === 'teams' ? 'Squadre' : 'Giocatori'}</Badge>
                        </div>
                        <h3 className="font-semibold">{comp.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {getStatusLabel(comp.status)}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent aria-label="Membri" value="members">
            <Card>
              <CardContent className="p-4">
                <div className="mb-4 flex items-start justify-between gap-2">
                  <CardLegend />
                  <div className="flex shrink-0 rounded-md border p-0.5" role="group" aria-label="Vista membri">
                    {[['cards', 'Card'], ['list', 'Lista']].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={membersView === key}
                        onClick={() => setMembersView(key)}
                        className={`rounded px-3 py-1 text-sm font-medium ${membersView === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {membersView === 'cards' ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {members.map((member) => (
                      <FifaCard
                        key={member.member_id}
                        player={member.user}
                        attributes={member.attributes}
                        catalog={traitsCatalog}
                        onClick={() => setProfileUserId(member.user_id)}
                      />
                    ))}
                  </div>
                ) : (
                <div className="space-y-2">
                  {members.map((member) => {
                    const isMemberOwner = member.user_id === league?.created_by;
                    const canManage = isOwner && !isMemberOwner;
                    return (
                      <div 
                        key={member.member_id}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors cursor-pointer"
                        onClick={() => setProfileUserId(member.user_id)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={member.user?.picture} />
                            <AvatarFallback>{getInitials(member.user?.name)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium">{member.user?.name}</p>
                              {member.user?.nickname && (
                                <span className="text-xs text-primary italic">&laquo;{member.user.nickname}&raquo;</span>
                              )}
                              {isMemberOwner && <Crown className="h-3.5 w-3.5 text-yellow-500" title="Creatore" />}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Membro dal {formatDate(member.joined_at)}
                            </p>
                            {renderMemberMeta(member)}
                          </div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Badge variant="outline">{getRoleLabel(member.role)}</Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setProfileUserId(member.user_id)}
                            title="Apri la scheda giocatore"
                            data-testid="open-player-card"
                          >
                            <IdCard className="h-4 w-4 text-primary" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEditProfileMember(member)}
                              title="Modifica stelle, forma e tag caratteristici"
                            >
                              <Tag className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          {canManage && (
                            <div className="flex items-center gap-1">
                              {member.role === 'player' ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleChangeRole(member.user_id, 'admin')}
                                  title="Promuovi ad Admin"
                                >
                                  <ShieldCheck className="h-4 w-4 text-green-600" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleChangeRole(member.user_id, 'player')}
                                  title="Rimuovi Admin"
                                >
                                  <ShieldOff className="h-4 w-4 text-orange-500" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleKickMember(member.user_id, member.user?.name)}
                                title="Espelli dalla lega"
                              >
                                <UserX className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="matches">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Tutte le Partite</CardTitle>
                {isAdmin && (
                  <Link to={`/matches/new?league=${id}`}>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Nuova Partita
                    </Button>
                  </Link>
                )}
              </CardHeader>
              <CardContent>
                {matches.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nessuna partita in questa lega
                  </p>
                ) : (
                  <div className="space-y-3">
                    {matches.map((match) => (
                      <Link 
                        key={match.match_id} 
                        to={`/matches/${match.match_id}`}
                        className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getStatusColorClass(match.status)}>
                              {getStatusLabel(match.status)}
                            </Badge>
                          </div>
                          <p className="font-medium">{match.title}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(match.date_time)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {match.location_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {match.participants_count}/{match.max_participants}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && (
            <TabsContent aria-label="Ripetizioni" value="series">
              <LeagueSeriesManager leagueId={id} />
            </TabsContent>
          )}

          {/* Admin Tab */}
          {isAdmin && (
            <TabsContent aria-label="Gestione" value="admin">
              <div className="space-y-6">
                <ScoringSettings leagueId={id} />
                <PointAdjustments leagueId={id} members={members} />
                <MemberStatsEditor leagueId={id} members={members} onUpdate={() => window.location.reload()} />
                <LeagueImportPanel leagueId={id} />
                <LeagueMembersManager 
                  sportId={league?.sport_id}
                  leagueId={id} 
                  leagueName={league.name}
                  onMembersChange={setMembers}
                />
                <SeasonReset
                  leagueId={id}
                  leagueName={league.name}
                  seasonStartedAt={league.current_season_started_at || league.created_at}
                />
              </div>
            </TabsContent>
          )}
          </Tabs>
        </div>
      </div>

      {/* Join Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unisciti a {league.name}</DialogTitle>
          </DialogHeader>
          
          {!league.is_public && (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Questa è una lega privata. Inserisci il codice invito per unirti.
              </p>
              <div className="space-y-2">
                <Label htmlFor="invite-code">Codice Invito</Label>
                <Input
                  id="invite-code"
                  placeholder="Es: ABC123XY"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  data-testid="invite-code-input"
                />
              </div>
            </div>
          )}
          
          {league.is_public && (
            <p className="text-muted-foreground">
              Clicca &quot;Unisciti&quot; per entrare nella lega.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleJoin} 
              disabled={joining || (!league.is_public && !inviteCode)}
              data-testid="confirm-join-btn"
            >
              {joining ? 'Invio...' : 'Unisciti'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Player Profile Dialog */}
      <PlayerProfileDialog
        userId={profileUserId}
        open={!!profileUserId}
        onOpenChange={(open) => !open && setProfileUserId(null)}
      />

      {/* Edit member profile (admin: skill + traits) */}
      <MemberProfileDialog
        sportId={league?.sport_id}
        open={!!editProfileMember}
        onOpenChange={(open) => !open && setEditProfileMember(null)}
        leagueId={id}
        member={editProfileMember}
        onSaved={handleProfileSaved}
      />
    </div>
  );
};

export default LeagueDetailPage;
